import { ApiSuccessSchema } from "../../common/response.schema";
import {
  AgentMemoryReadPathVarsSchema,
  AgentMemoryReadResponseSchema,
  MemorySchemaDeclareBodySchema,
  MemorySchemaListParamsSchema,
  MemorySchemaPathVarsSchema,
  MemorySchemaScopeDeclarationsSchema,
  MemorySchemaVersionViewSchema
} from "./schemas";

/**
 * Agent memory — the session (browser) surface.
 *
 * 🔴 ONE DESCRIPTOR, NOT TWO, AND THE REASON IS THAT TWO READS ARE TWO INSTANTS.
 * The obvious split is "entries for the sidebar" and "the rendered block for the
 * model". It is the wrong seam twice over:
 *
 * 1. **The model does not come through here.** `RenderAgentMemoryBlockUseCase` is
 *    consumed in-process by prompt assembly. An HTTP route serving "the block" has no
 *    model consumer at all; its readers are browsers and operators.
 * 2. **The interesting fact is the DISAGREEMENT between the two halves, and split
 *    across two requests it stops being observable.** `entries.length` against
 *    `block.renderedEntries` is where a human sees the budget drop, and the served
 *    `integrity` verdict on the same response is the drift answer; read at two instants
 *    either can differ because a write landed in between, which is indistinguishable
 *    from what each exists to surface. One port read, one response — and both mean
 *    something.
 *
 * There is also only ONE read port. `FindAgentMemoryBySubjectAndOrgPort` returns
 * `{ memory, entries }` in a single call, so a second endpoint would not even be a
 * second query — it would be the same query twice.
 *
 * ⚠️ NO ENTRY-WRITE DESCRIPTOR. `PutAgentMemoryEntryUseCase` exists and is deliberately
 * not exposed here: that door is what an AGENT drives through a tool, and putting it on
 * a browser surface is a product decision nobody has made. A read route is not a step
 * towards it, and neither are the three declaration routes below — they write
 * `MemorySchema`, which is a different table with a different author.
 *
 * ## 🔴 THE THREE `*Schema` DESCRIPTORS ARE WHAT MAKES THE REST OF THIS FEATURE REACHABLE
 *
 * `assertKeyIsDeclared` refuses every entry write for a scope with no ACTIVE
 * `MemorySchema`. Until these landed nothing in the product could create one — the only
 * non-test caller of the `memorySchema` delegate was that `findFirst` — so the shipped
 * write door and the shipped model-facing tool were reachable and permanently inert.
 * Production 2026-08-31: 0 `MemorySchema` rows, 0 activated, 0 `AgentMemoryEntry`, 0
 * `AgentMemory` (control: 1,041 `Workflow`).
 *
 * ⚠️ THEY LIVE ON THEIR OWN BASE PATH (`/api/agent-memory-schemas`), NOT UNDER
 * `/api/agent-memory`, AND THAT IS THE ROUTE-ORDER HAZARD RATHER THAN taste.
 * `AgentMemoryController`'s header records that its single `GET :scope/:subjectId` is
 * safe precisely because it is the only route in that namespace and is two segments; a
 * `GET /api/agent-memory/schemas/:something` is the same shape to a router and would
 * have to be declared ABOVE it and kept there forever. A sibling namespace cannot
 * collide at all, and the property that header asks to preserve stays preserved.
 *
 * ⚠️ ACTIVATE AND DEACTIVATE ARE `POST` ON A SUB-RESOURCE, NOT A `PATCH` ON
 * `activatedAt`. A `PATCH { activatedAt }` would advertise a column a caller may set to
 * an arbitrary instant, and the operation is not a field assignment: activating one
 * version DEACTIVATES the one currently live, under a lock, in one transaction. The verb
 * names the transition, and the timestamp is the server's.
 *
 * ## 🔴 `ListSchemas` IS WHAT MAKES AN EDIT SAFE, AND ITS ABSENCE WAS DESTRUCTIVE
 *
 * `MemorySchemaDeclareBodySchema.keys` is the WHOLE field list and activating a version
 * stands the live one down, so *"add a field"* is a full re-declaration. Without a read
 * the only source for the current list is the builder's memory, and a field left out is
 * silently un-declared — after which `assertKeyIsDeclared` refuses every agent write to
 * that key, with nothing at the edit site saying so. A destructive edit with no error
 * anywhere.
 *
 * It also recovers a `memorySchemaId`. Before it, the id existed only in the `declare`
 * response body: a declare that succeeded followed by an activate that failed stranded a
 * row nothing could ever find again, so declare-and-activate had to be one continuous
 * act rather than two sessions.
 *
 * ⚠️ IT IS A `GET` ON THE COLLECTION ROOT WITH THE SCOPE IN THE QUERY STRING, WHICH ADDS
 * NO PATH SEGMENT AND SO ADDS NO ROUTE-ORDER HAZARD AT ALL. A `GET :scope` here would be
 * the same shape to a router as the two `POST :memorySchemaId/...` routes are on their
 * verb, and would start the ordering constraint {@link AgentMemoryController}'s header
 * asks the sibling namespace to keep avoiding. There is exactly one `GET` in this
 * namespace and it is on the root, so nothing can shadow it and it can shadow nothing.
 *
 * ⚠️ ONE DESCRIPTOR, NOT TWO, FOR THE SAME REASON `Read` IS ONE. *"Which version is
 * live"* and *"what versions exist"* are one port read and one snapshot; split across two
 * requests, a version that appears active in one answer and inactive in the other is
 * indistinguishable from a concurrent activation.
 */
export const ZAgentMemory = {
  Read: {
    method: "GET" as const,
    path: "/api/agent-memory/:scope/:subjectId",
    PathVars: AgentMemoryReadPathVarsSchema,
    Response: ApiSuccessSchema(AgentMemoryReadResponseSchema)
  },

  ListSchemas: {
    method: "GET" as const,
    path: "/api/agent-memory-schemas",
    Params: MemorySchemaListParamsSchema,
    Response: ApiSuccessSchema(MemorySchemaScopeDeclarationsSchema)
  },

  DeclareSchema: {
    method: "POST" as const,
    path: "/api/agent-memory-schemas",
    Body: MemorySchemaDeclareBodySchema,
    Response: ApiSuccessSchema(MemorySchemaVersionViewSchema)
  },

  ActivateSchema: {
    method: "POST" as const,
    path: "/api/agent-memory-schemas/:memorySchemaId/activate",
    PathVars: MemorySchemaPathVarsSchema,
    Response: ApiSuccessSchema(MemorySchemaVersionViewSchema)
  },

  DeactivateSchema: {
    method: "POST" as const,
    path: "/api/agent-memory-schemas/:memorySchemaId/deactivate",
    PathVars: MemorySchemaPathVarsSchema,
    Response: ApiSuccessSchema(MemorySchemaVersionViewSchema)
  }
} as const;
